/**
 * Part Repository tab for the Call Off module.
 * Displays catalog parts with supplier assignments, search, pagination,
 * and allows assigning/removing suppliers from parts.
 *
 * Supports:
 * - Bulk assign via checkbox selection + "Assign Suppliers" button
 * - Per-part assign via double-click on a row (opens modal for that part)
 * - Searchable supplier dropdown in both assignment flows
 */
import { useState, useEffect, useCallback } from 'react';
import {
  Loader2, Search, Database,
  Users, UserPlus, Upload, X,
} from 'lucide-react';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Pagination } from '../../ui/pagination';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui/dialog';
import {
  TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Card, CardContent } from '../../ui/card';
import { toast } from 'sonner';
import { callOffAPI } from '../../../lib/api';
import { partsAPI } from '../../../lib/api/parts';
import type { PartCatalogItem, PartRepositoryStats } from '../../../lib/api/types';
import type { PartSupplierUploadResponse } from '../../../lib/api/types/parts';
import SupplierSearchList, { type SupplierOption } from './SupplierSearchList';
import SupplierAssignmentUploadDialog from './SupplierAssignmentUploadDialog';

export default function PartRepositoryTab() {
  const [items, setItems] = useState<PartCatalogItem[]>([]);
  const [stats, setStats] = useState<PartRepositoryStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterAssigned, setFilterAssigned] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  // Bulk supplier assignment state
  const [selectedParts, setSelectedParts] = useState<Set<number>>(new Set());
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState<Set<number>>(new Set());
  const [assigning, setAssigning] = useState(false);

  // Per-part assignment state (double-click modal)
  const [partDialogOpen, setPartDialogOpen] = useState(false);
  const [activePart, setActivePart] = useState<PartCatalogItem | null>(null);
  const [partSelectedSuppliers, setPartSelectedSuppliers] = useState<Set<number>>(new Set());
  const [partAssigning, setPartAssigning] = useState(false);

  // Supplier-assignment Excel upload state
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<PartSupplierUploadResponse | null>(null);

  const fetchParts = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, page_size: pageSize };
      if (search) params['search'] = search;
      if (filterAssigned === 'assigned') params['is_assigned'] = 'true';
      if (filterAssigned === 'unassigned') params['is_assigned'] = 'false';
      const res = await callOffAPI.getParts(params);
      setItems(res.data.items);
      setTotalCount(res.data.total_count);
    } catch {
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterAssigned]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await callOffAPI.getPartStats();
      setStats(res.data);
    } catch { /* ignore */ }
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const res = await callOffAPI.getSuppliers();
      const list = res.data ?? [];
      setSuppliers(list.map((s: Record<string, number | string>) => ({
        id: s['id'] as number,
        name: (s['name'] as string) || `Supplier ${s['id']}`,
      })));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  useEffect(() => {
    const timer = setTimeout(fetchParts, 300);
    return () => clearTimeout(timer);
  }, [fetchParts]);

  useEffect(() => { setPage(1); }, [search, filterAssigned]);

  const togglePart = (id: number) => {
    setSelectedParts(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedParts.size === items.length) {
      setSelectedParts(new Set());
    } else {
      setSelectedParts(new Set(items.map(i => i.id)));
    }
  };

  // Bulk assign handler
  const handleBulkAssign = async () => {
    if (selectedParts.size === 0 || selectedSuppliers.size === 0) return;
    setAssigning(true);
    try {
      await callOffAPI.bulkAssignSuppliers(
        Array.from(selectedParts),
        Array.from(selectedSuppliers),
      );
      toast.success(`Assigned ${selectedSuppliers.size} supplier(s) to ${selectedParts.size} part(s)`);
      setAssignDialogOpen(false);
      setSelectedParts(new Set());
      setSelectedSuppliers(new Set());
      fetchParts();
      fetchStats();
    } catch {
      toast.error('Failed to assign suppliers');
    } finally {
      setAssigning(false);
    }
  };

  // Per-part assign handler (double-click row)
  const handleRowDoubleClick = (part: PartCatalogItem) => {
    setActivePart(part);
    setPartSelectedSuppliers(new Set(part.suppliers.map(s => s.id)));
    setPartDialogOpen(true);
  };

  const handlePartAssign = async () => {
    if (!activePart) return;
    setPartAssigning(true);
    try {
      await callOffAPI.assignPartSuppliers(
        activePart.id,
        Array.from(partSelectedSuppliers),
      );
      toast.success(`Updated suppliers for ${activePart.part_number}`);
      setPartDialogOpen(false);
      setActivePart(null);
      setPartSelectedSuppliers(new Set());
      fetchParts();
      fetchStats();
    } catch {
      toast.error('Failed to assign suppliers');
    } finally {
      setPartAssigning(false);
    }
  };

  const handleSupplierUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const response = await partsAPI.uploadSupplierAssignments(uploadFile);
      setUploadResult(response.data);
      fetchParts();
      fetchStats();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Upload failed';
      const axiosErr = err as Record<string, Record<string, Record<string, string>>>;
      setUploadResult({
        total_rows: 0,
        assignments_created: 0,
        assignments_updated: 0,
        assignments_deactivated: 0,
        default_suppliers_set: 0,
        errors: [{ sheet: '', row: 0, error: axiosErr?.['response']?.['data']?.['error'] || message }],
        error_count: 1,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveSupplier = async (partId: number) => {
    try {
      await callOffAPI.clearPartSuppliers(partId);
      toast.success('Suppliers removed');
      fetchParts();
      fetchStats();
    } catch {
      toast.error('Failed to remove suppliers');
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total Parts</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats.assigned}</p>
              <p className="text-xs text-muted-foreground">Assigned</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.unassigned}</p>
              <p className="text-xs text-muted-foreground">Unassigned</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by part number or description..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterAssigned} onValueChange={setFilterAssigned}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Parts</SelectItem>
              <SelectItem value="assigned">Assigned</SelectItem>
              <SelectItem value="unassigned">Unassigned</SelectItem>
            </SelectContent>
          </Select>
          {selectedParts.size > 0 && (
            <Button onClick={() => setAssignDialogOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Assign Suppliers ({selectedParts.size})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => {
              setUploadOpen(true);
              setUploadFile(null);
              setUploadResult(null);
            }}
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Assignments
          </Button>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-xs text-muted-foreground">Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-8 text-sm border border-border rounded-lg px-2 bg-background focus:outline-none focus:ring-2 focus:ring-primary"
          >
            {[50, 100, 200, 500, 1000].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>{search ? 'No parts match your search' : 'No parts in the catalog'}</p>
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-auto max-h-[calc(100vh-340px)]">
            <table className="w-full caption-bottom text-sm">
              <TableHeader className="sticky top-0 z-20 bg-muted shadow-[0_2px_4px_-2px_rgba(0,0,0,0.08)]">
                <TableRow className="hover:bg-muted border-b">
                  <TableHead className="w-10 font-semibold text-foreground">
                    <Checkbox
                      checked={selectedParts.size === items.length && items.length > 0}
                      onCheckedChange={toggleAll}
                    />
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">Part Number</TableHead>
                  <TableHead className="font-semibold text-foreground">Description</TableHead>
                  <TableHead className="font-semibold text-foreground">Alt Part No.</TableHead>
                  <TableHead className="font-semibold text-foreground text-right">Pallet Qty</TableHead>
                  <TableHead className="font-semibold text-foreground text-right">Load Qty</TableHead>
                  <TableHead className="font-semibold text-foreground">Suppliers</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((part) => (
                  <TableRow
                    key={part.id}
                    className="cursor-pointer"
                    onDoubleClick={() => handleRowDoubleClick(part)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedParts.has(part.id)}
                        onCheckedChange={() => togglePart(part.id)}
                      />
                    </TableCell>
                    <TableCell className="font-medium">{part.part_number}</TableCell>
                    <TableCell>{part.description}</TableCell>
                    <TableCell className="text-muted-foreground">{part.alt_part_number || '-'}</TableCell>
                    <TableCell className="text-right">{part.pallet_qty}</TableCell>
                    <TableCell className="text-right">{part.load_qty}</TableCell>
                    <TableCell>
                      {part.suppliers.length === 0 ? (
                        <span className="text-muted-foreground text-sm">Unassigned</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {part.suppliers.map((s) => (
                            <Badge key={s.id} variant="secondary" className="text-xs">
                              <Users className="h-3 w-3 mr-1" />
                              {s.name}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {part.suppliers.length > 0 && (
                        <Button
                          variant="ghost" size="icon" className="h-7 w-7"
                          onClick={() => handleRemoveSupplier(part.id)}
                          title="Remove all suppliers"
                        >
                          <X className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            itemsPerPage={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            itemLabel="parts"
          />
        </>
      )}

      {/* Bulk Assign Suppliers Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Assign Suppliers to {selectedParts.size} Part(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Search and select suppliers to assign:
            </p>
            <SupplierSearchList
              suppliers={suppliers}
              selected={selectedSuppliers}
              onToggle={(id) => {
                setSelectedSuppliers(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              }}
            />
            {selectedSuppliers.size > 0 && (
              <p className="text-sm text-muted-foreground">
                {selectedSuppliers.size} supplier(s) selected
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkAssign} disabled={assigning || selectedSuppliers.size === 0}>
              {assigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier-assignment Excel upload */}
      <SupplierAssignmentUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        uploadFile={uploadFile}
        onFileSelected={(f) => { setUploadFile(f); setUploadResult(null); }}
        uploading={uploading}
        uploadResult={uploadResult}
        onUpload={handleSupplierUpload}
        onDone={() => { setUploadOpen(false); setUploadResult(null); setUploadFile(null); }}
      />

      {/* Per-Part Assign Suppliers Dialog (double-click) */}
      <Dialog open={partDialogOpen} onOpenChange={setPartDialogOpen}>
        <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto scrollbar-none">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Manage Suppliers
            </DialogTitle>
          </DialogHeader>
          {activePart && (
            <div className="space-y-3 py-2">
              <div className="rounded-lg bg-muted/50 p-3">
                <p className="font-medium">{activePart.part_number}</p>
                <p className="text-sm text-muted-foreground">{activePart.description}</p>
              </div>
              <p className="text-sm text-muted-foreground">
                Search and select suppliers for this part:
              </p>
              <SupplierSearchList
                suppliers={suppliers}
                selected={partSelectedSuppliers}
                onToggle={(id) => {
                  setPartSelectedSuppliers(prev => {
                    const next = new Set(prev);
                    if (next.has(id)) next.delete(id); else next.add(id);
                    return next;
                  });
                }}
              />
              {partSelectedSuppliers.size > 0 && (
                <p className="text-sm text-muted-foreground">
                  {partSelectedSuppliers.size} supplier(s) selected
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPartDialogOpen(false)}>Cancel</Button>
            <Button onClick={handlePartAssign} disabled={partAssigning}>
              {partAssigning && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
